// src/pages/outsourcing_management/inquiryFormValues.ts
//
// The one value shape behind both halves of InquiryDialog. It is
// components/Inquiry's InquiryFormValues unchanged, because buildInquiryPayload
// and useEmailPreview are written against it and both are still the right tools
// for the create half.
import { useContext } from "react";
import { useQuery } from "react-query";
import { CartItemType, CartType } from "types/CartTypes";
import { OrderItemType, OrderType } from "types/OrderTypes";
import { InquiryDetailType, InquiryType, InquiryTypeEnum } from "types/InquiryTypes";
import { ProductTypeEnum } from "types/products/ProductCommandsType";
import { apiGetOrder } from "util/network/Orders";
import { apiGetCart } from "util/network/Carts";
import AppStateContext from "contexts/AppStateContext";
import { InquiryFormValues } from "components/Inquiry/types";
import { getInitialDeadline } from "components/Inquiry/utils/inquiryPayload";
import { SelectedItem } from "components/Inquiry/hooks/useInquiryItems";

export type { InquiryFormValues };

export const DELIVERY_METHODS = ["Delivery", "Pickup"];

// An unset deadline comes back as the epoch rather than null, so it has to be
// recognised or the date input opens on 1970 (same rule as the row and the
// facts grid).
export const toDateInput = (value: string | null | undefined) => {
  if (!value) return "";
  const date = value.slice(0, 10);
  return date === "1970-01-01" ? "" : date;
};

export const initialCreateValues = (
  prodObjIn: OrderType | CartType | undefined,
  prodType: "order" | "cart"
): InquiryFormValues => ({
  type: InquiryTypeEnum.CNC,
  partnerId: undefined,
  deadline: getInitialDeadline(prodObjIn, prodType),
  deliveryMethod: "",
  message: "",
  title: "",
  // Creating an inquiry never emails: it is saved and then sent by hand from
  // the inquiry's own Send to supplier button, so the draft can be read once
  // more before it leaves. Editing one does not email either - see
  // InquiryEditForm.
  sendEmail: false,
  sendFromEmail: "info@easypartz.com",
  deliveryTime: "",
  notes: "",
});

export const initialEditValues = (
  inquiry: InquiryType
): InquiryFormValues => ({
  type: inquiry.type,
  partnerId: inquiry.partnerId ?? undefined,
  deadline: toDateInput(inquiry.deliveryDate),
  deliveryMethod: inquiry.deliveryMethod ?? "",
  message: inquiry.message ?? "",
  title: inquiry.title ?? "",
  sendEmail: false,
  sendFromEmail: "info@easypartz.com",
  deliveryTime: inquiry.deliveryTime ?? "",
  notes: inquiry.notes ?? "",
});

// ─── Seeding the item editor from an inquiry ─────────────────────────────────
//
// useInquiryItems has always taken initialSelected / initialCustomItems for
// "update mode" and nothing ever passed them. These two split an inquiry's
// lines the same way InquiryItemsTable does when it picks a row component: an
// OTHER product is a free-text line and belongs to the custom table, anything
// else came from an order or a quote and belongs to the picker.

/** Lines that came from an order or a quote, in the shape the picker ticks. */
export const selectedFromInquiry = (inquiry: InquiryDetailType): SelectedItem[] =>
  inquiry.items
    .filter(
      (item) => item.product && item.product.productType !== ProductTypeEnum.OTHER
    )
    .map((item) => ({
      id: item.id,
      productId: item.product!.id,
      orderItemId: item.orderItemId,
      quoteItemId: item.quoteItemId,
      quantity: item.quantity,
    }));

/**
 * Free-text lines, in the shape InquiryItemsTable builds for a new one.
 * `id` is the product's, not the inquiry line's: the row writes the name,
 * description and files straight back onto that product.
 */
export const customItemsFromInquiry = (
  inquiry: InquiryDetailType
): (OrderItemType | CartItemType)[] =>
  inquiry.items
    .filter(
      (item) => item.product && item.product.productType === ProductTypeEnum.OTHER
    )
    .map(
      (item) =>
        ({
          id: item.product!.id,
          product: item.product!,
          quantity: item.quantity,
          name: item.product!.name,
          description: item.comment ?? "",
        } as unknown as OrderItemType)
    );

/**
 * The order or quote an existing inquiry hangs off, fetched so the item picker
 * has something to list. `prodObj` is undefined for an inquiry raised directly
 * or from a requisition - those only ever had custom lines.
 *
 * `isError` is returned rather than swallowed: the editor is hidden until the
 * order is in hand, and a failed fetch left it hidden behind "Loading the order
 * behind these lines..." for as long as the dialog stayed open. A caption that
 * is never going to come true is worse than an error.
 */
export const useInquiryProdObj = (inquiry: InquiryType | undefined) => {
  const { token } = useContext(AppStateContext);
  const orderId = inquiry?.orderId;
  const quoteId = inquiry?.quoteId;
  const query = useQuery<OrderType | CartType | undefined>(
    ["inquiryProdObj", orderId ?? null, quoteId ?? null],
    () =>
      orderId
        ? apiGetOrder(token, orderId)
        : quoteId
        ? apiGetCart(token, quoteId)
        : Promise.resolve(undefined),
    { enabled: !!token && (!!orderId || !!quoteId), staleTime: 5 * 60 * 1000 }
  );
  return { prodObj: query.data, isError: query.isError };
};
